import { getEnvConfig } from '@hbs-us/contracts';
import posthog, { type CaptureResult } from 'posthog-js';
import type { ErrorInfo } from 'react';

import type { Session } from '../auth/authClient.js';
import { getAppEnv } from '../lib/env.js';

type ActorType = 'tenant_user' | 'platform_admin' | 'impersonation' | 'system';

export const ANALYTICS_EVENTS = {
  dashboardPageViewed: 'dashboard_page_viewed',
  errorBoundaryRendered: 'error_boundary_rendered',
  notificationCenterOpened: 'notification_center_opened',
} as const;

export const ANALYTICS_MASK_CLASS = 'ph-no-capture';
export const ANALYTICS_TEXT_MASK_CLASS = 'ph-mask';

const PROJECT_TOKEN = String(import.meta.env.VITE_POSTHOG_KEY ?? '').trim();

/**
 * The build-safe default this file used to fall back to. It is not a live project token, so it is
 * rejected by name — deleting the fallback is not enough while a stale CI variable can still be
 * set to the same literal.
 */
const RETIRED_PLACEHOLDER_TOKEN = 'phc_qelm_dev_public_placeholder';

export function isAnalyticsEnabled(): boolean {
  return PROJECT_TOKEN.length > 0 && PROJECT_TOKEN !== RETIRED_PLACEHOLDER_TOKEN;
}

const SENSITIVE_ROUTE_IDS = new Set([
  'activity',
  'billing',
  'employees',
  'employee_detail',
  'estimates',
  'estimate_detail',
  'invoice_detail',
  'invoices',
  'jobs',
  'job_detail',
  'monthly_bills',
  'payroll',
  'payroll_history',
  'payroll_run_detail',
  'profitability',
  'settings',
  'settings_notifications',
  'support',
  'timesheets',
  'users',
  'users_permissions',
]);

let initialized = false;
let actorType: ActorType = 'tenant_user';

type OptionalAnalyticsContext = {
  analytics?: {
    userAnalyticsId?: string | null;
    tenantAnalyticsId?: string | null;
    impersonatedUserAnalyticsId?: string | null;
    impersonatedTenantAnalyticsId?: string | null;
  } | null;
};

function posthogHostUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).posthogHost}`;
}

/**
 * posthog-js stamps the full `location.href` onto EVERY event as `$current_url`, and the session's
 * entry URL and referrer onto every later one. A search box puts whatever was typed into the query
 * string — `/invoices?q=<a customer email>` — so the query string never leaves the browser. Same `split('?')[0]`
 * shape the network-payload stripper uses.
 */
const URL_PROPERTY_KEYS = [
  '$current_url',
  '$session_entry_url',
  '$referrer',
  '$initial_referrer',
  '$initial_current_url',
] as const;

function scrubUrls(source: Record<string, unknown>): Record<string, unknown> {
  const scrubbed = { ...source };
  for (const key of URL_PROPERTY_KEYS) {
    const value = scrubbed[key];
    if (typeof value === 'string') scrubbed[key] = value.split('?')[0];
  }
  return scrubbed;
}

function scrubSetOnce(value: unknown): unknown {
  return value && typeof value === 'object' ? scrubUrls(value as Record<string, unknown>) : value;
}

// The SDK puts `$set_once` in two places: inside `properties`, and as a top-level block beside
// `properties` on the first event of each page load once a person is identified. Scrub both.
function stripUrlQueryStrings(result: CaptureResult | null): CaptureResult | null {
  if (!result?.properties) return result;
  const properties = scrubUrls(result.properties as Record<string, unknown>);
  if (properties.$set_once) properties.$set_once = scrubSetOnce(properties.$set_once);
  const scrubbed = { ...result, properties } as CaptureResult;
  if (scrubbed.$set_once) scrubbed.$set_once = scrubSetOnce(scrubbed.$set_once) as never;
  return scrubbed;
}

function stripNetworkPayload(request: {
  url?: string;
  requestBody?: unknown;
  responseBody?: unknown;
  requestHeaders?: unknown;
  responseHeaders?: unknown;
}) {
  return {
    ...request,
    url: typeof request.url === 'string' ? request.url.split('?')[0] : request.url,
    requestBody: undefined,
    responseBody: undefined,
    requestHeaders: undefined,
    responseHeaders: undefined,
  };
}

export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined' || !isAnalyticsEnabled()) return;
  posthog.init(PROJECT_TOKEN, {
    api_host: posthogHostUrl(),
    ui_host: 'https://us.posthog.com',
    before_send: stripUrlQueryStrings,
    autocapture: false,
    capture_pageview: false,
    // The project's remote config can switch these on behind the init's back. The chat widget
    // duplicated QELM support on every page and failed its own ticket fetch; dead-click capture
    // records the clicked element's text, which is what `autocapture: false` keeps off the wire.
    disable_conversations: true,
    capture_dead_clicks: false,
    // Error Tracking (OBS-06): see the note in apps/admin/src/app/analytics.ts. Console
    // errors stay off because they bill against the same exception allowance.
    capture_exceptions: {
      // Error text can contain customer names, addresses and money. The explicit boundary event
      // below emits only a type and a component-stack fingerprint, so raw SDK exception capture
      // stays off unless PostHog gains an enforceable exception scrubber.
      capture_unhandled_errors: false,
      capture_unhandled_rejections: false,
      capture_console_errors: false,
    },
    disable_session_recording: true,
    session_recording: {
      blockClass: ANALYTICS_MASK_CLASS,
      maskAllInputs: true,
      maskInputOptions: {
        color: true,
        date: true,
        email: true,
        number: true,
        password: true,
        search: true,
        tel: true,
        text: true,
        textarea: true,
        url: true,
      },
      maskTextClass: ANALYTICS_TEXT_MASK_CLASS,
      maskTextSelector: '*',
      maskCapturedNetworkRequestFn: stripNetworkPayload,
    },
  } as unknown as Parameters<typeof posthog.init>[1]);
  initialized = true;
}

export function routeIdForPath(pathname: string): string {
  const path = pathname.replace(/^\/+|\/+$/g, '');
  if (!path) return 'reports';
  if (path.startsWith('settings/notifications')) return 'settings_notifications';
  if (path.startsWith('invoices/') && path !== 'invoices/new') return 'invoice_detail';
  if (path.startsWith('estimates/') && path !== 'estimates/new') return 'estimate_detail';
  if (path.startsWith('employees/') && path !== 'employees/new') return 'employee_detail';
  if (path.startsWith('jobs/') && path !== 'jobs/new') return 'job_detail';
  if (path.startsWith('payroll/runs/')) return 'payroll_run_detail';
  if (path.startsWith('payroll/history')) return 'payroll_history';
  if (path.startsWith('users/permissions')) return 'users_permissions';
  if (path.startsWith('monthly-bills')) return 'monthly_bills';
  if (path.startsWith('service-agreements')) return 'service_agreements';
  return path.split('/')[0]?.replaceAll('-', '_') || 'unknown';
}

export function analyticsMaskClassForRoute(routeId: string): string {
  return SENSITIVE_ROUTE_IDS.has(routeId) ? ANALYTICS_MASK_CLASS : '';
}

export function hydrateAnalyticsIdentity(session: Session): void {
  initAnalytics();
  if (!initialized) return;
  const analytics = (session as Session & OptionalAnalyticsContext).analytics;
  actorType = session.impersonation ? 'impersonation' : 'tenant_user';

  const distinctId = session.impersonation
    ? analytics?.impersonatedUserAnalyticsId
    : analytics?.userAnalyticsId;
  const tenantId = session.impersonation
    ? analytics?.impersonatedTenantAnalyticsId
    : analytics?.tenantAnalyticsId;

  if (distinctId) {
    posthog.identify(distinctId, {
      actor_type: actorType,
      env: getAppEnv(),
      role: session.user.role,
    });
  }
  if (tenantId) {
    posthog.group('tenant', tenantId, { env: getAppEnv() });
  }
}

export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
  stopReplay();
  actorType = 'tenant_user';
}

export function capturePageView(routeId: string): void {
  initAnalytics();
  if (!initialized) return;
  posthog.capture(ANALYTICS_EVENTS.dashboardPageViewed, {
    actor_type: actorType,
    env: getAppEnv(),
    route_id: routeId,
    surface: 'dashboard',
  });
}

export function captureNotificationCenterOpened(): void {
  initAnalytics();
  if (!initialized) return;
  posthog.capture(ANALYTICS_EVENTS.notificationCenterOpened, {
    actor_type: actorType,
    env: getAppEnv(),
    route_id: 'notifications',
    surface: 'dashboard',
  });
}

export function startSafeReplay(routeId: string): void {
  if (SENSITIVE_ROUTE_IDS.has(routeId)) return;
  initAnalytics();
  if (!initialized) return;
  posthog.startSessionRecording?.();
}

export function stopReplay(): void {
  posthog.stopSessionRecording?.();
}

function fingerprint(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(16);
}

export function captureErrorBoundary(error: Error, info: ErrorInfo, routeId: string): void {
  initAnalytics();
  if (!initialized) return;
  const properties = {
    actor_type: actorType,
    component_stack_fingerprint: fingerprint(info.componentStack ?? ''),
    env: getAppEnv(),
    error_type: error.name || 'Error',
    route_id: routeId,
    surface: 'dashboard',
  };
  posthog.capture(ANALYTICS_EVENTS.errorBoundaryRendered, properties);
}
